/** Shared copy/formatting for tee-time surfaces (card + list row). */

export function formatTime12hSingleLine(dateTime: Date): string {
  return dateTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Split 12h time — hero clock vs caption-scale meridiem (card + list row). */
export function splitTime12h(dateTime: Date): { clock: string; meridiem: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(dateTime);
  let clock = '';
  let meridiem = '';
  for (const { type, value } of parts) {
    if (type === 'dayPeriod') meridiem = value;
    else clock += value;
  }
  return { clock: clock.trimEnd(), meridiem };
}

export type TeeTimePromo = {
  type: 'percentage' | 'fixed';
  value: number;
  discountGreenFee?: boolean;
  discountCart?: boolean;
};

/** Headline next to pricing meter chip — mirrors demand `12%` / `Under 1%` slot. */
export function formatStaffPromoMeterHeadline(promo: TeeTimePromo): string {
  return promo.type === 'percentage' ? `${promo.value}%` : `$${promo.value}`;
}

export function formatStaffPromoLine(promo: TeeTimePromo): string {
  const promoValue =
    promo.type === 'percentage' ? `${promo.value}% off` : `$${promo.value} off`;
  const discountGreenFee = promo.discountGreenFee !== false;
  const discountCart = promo.discountCart !== false;
  const productLabels: string[] = [];
  if (discountGreenFee) productLabels.push('Green Fee');
  if (discountCart) productLabels.push('Cart');
  const productsText = productLabels.length > 0 ? productLabels.join(', ') : '';
  return productsText ? `${promoValue} ${productsText}` : promoValue;
}

export type MemberPlayerSummary = {
  spotNumber: number;
  firstName: string;
  lastName: string;
  customerType: string;
};

export function computeMemberLineDisplay(
  memberPlayers: MemberPlayerSummary[] | undefined,
  isMember: boolean,
): string | null {
  if (!memberPlayers || memberPlayers.length === 0 || !isMember) return null;
  const line = memberPlayers
    .sort((a, b) => a.spotNumber - b.spotNumber)
    .map((player) => {
      const isGuest =
        player.customerType === 'Guest' ||
        (player.firstName === 'Guest' && !player.lastName);
      const isPrivacyEnabled =
        player.firstName === 'Member' &&
        !player.lastName &&
        player.customerType !== 'Guest';
      const isNonMemberCustomer =
        !isGuest &&
        !isPrivacyEnabled &&
        !player.lastName &&
        player.customerType &&
        player.customerType !== 'Guest' &&
        player.firstName === player.customerType;

      if (isGuest) {
        return 'Guest';
      }
      if (isPrivacyEnabled || (!isMember && !isNonMemberCustomer)) {
        return player.customerType !== 'Guest' ? player.customerType : 'Member';
      }
      if (isNonMemberCustomer) {
        return player.customerType || player.firstName || 'Guest';
      }
      if (isMember && player.firstName) {
        const label = [player.firstName, player.lastName].filter(Boolean).join(' ').trim();
        if (label) return label;
      }
      return player.customerType || player.firstName || 'Guest';
    })
    .filter(Boolean)
    .join(', ');
  return line || null;
}
